/**
 * @fileoverview 未ログイン時のコンテンツの遮蔽に使われる CTA
 */
<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import ButtonSns from '@/components/button/ButtonSns.vue'
import {
  newUserSessionPath,
  newUserRegistrationPath,
  userFacebookOmniauthAuthorizePath,
  userGoogleOauth2OmniauthAuthorizePath,
} from '@/generated/railsRoutes'
import scrollUtil from '@/utilities/scrollUtil'

export default defineComponent({
  name: 'MaskContentForGuest',

  components: {
    ButtonSubmit,
    ButtonSns,
  },

  props: {
    /**
     * 会員登録を促すためのマスクかどうか
     */
    forSignUp: {
      type: Boolean,
      default: false,
    },

    hasFade: {
      type: Boolean,
      default: true,
    },

    hasHeadingSecondary: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const maskEl = ref<Maybe<HTMLElement>>()
    const maskRect = ref<Maybe<DOMRect>>()

    const headingLabel = computed<string>(() => {
      return props.forSignUp
        ? '続きを読むには、ニュースレター登録（簡単１分・無料）が必要です'
        : '続きを見るにはログインが必要です'
    })

    let cleanupScroll: (() => void) | null = null

    onMounted(() => {
      nextTick(() => {
        cleanupScroll = scrollUtil.throttle({
          callback: () => {
            if (!maskEl.value) return
            maskRect.value = maskEl.value.getBoundingClientRect()
          },
          wait: 30,
        })
      })
    })

    onBeforeUnmount(() => {
      cleanupScroll?.()
    })

    return {
      // routes
      newUserSessionPath,
      newUserRegistrationPath,
      userFacebookOmniauthAuthorizePath,
      userGoogleOauth2OmniauthAuthorizePath,

      // data
      maskEl,
      maskRect,

      // computed
      headingLabel,
    }
  },
})
</script>

<template>
  <section
    class="f-section__maskForGuest"
    :data-fade="hasFade">
    <div class="f-section__maskForGuest--body">
      <div
        ref="maskEl"
        class="l-unit__maskForGuest"
        :data-balloon="hasHeadingSecondary">
        <div class="l-unit__maskForGuest--header">
          <div class="o-heading__maskForGuest">
            <h2 class="o-heading__maskForGuest--item">
              <span class="o-heading__maskForGuest--label is-primary">
                <slot name="headingPrimary">
                  {{ headingLabel }}
                </slot>
              </span>
              <span
                v-if="hasHeadingSecondary"
                class="o-heading__maskForGuest--label is-secondary">
                <slot name="headingSecondary">
                  全てのコンテンツが無料で読めます
                </slot>
              </span>
            </h2>
          <!-- /.o-heading__maskForGuest --></div>
        <!-- /.l-unit__maskForGuest header --></div>

        <div class="l-unit__maskForGuest--action is-primary">
          <div class="o-list__signInCta">
            <ul class="o-list__signInCta--items">
              <li class="o-list__signInCta--item is-mail">
                <ButtonSubmit
                  :href="forSignUp ? newUserRegistrationPath() : newUserSessionPath()"
                  narrow-margin>
                  <i class="i-mail" />
                  メールアドレスで{{ forSignUp ? '登録' : 'ログイン' }}
                </ButtonSubmit>

                <div class="o-note">
                  <p class="o-note--item u-text__center u-text__x-small">
                    または他サービスIDで登録
                  </p>
                </div>
              </li>
              <li class="o-list__signInCta--item is-sns">
                <ButtonSns
                  :href="userFacebookOmniauthAuthorizePath()"
                  is-font-small
                  filled
                  service="facebook" />
              </li>
              <li class="o-list__signInCta--item is-sns">
                <ButtonSns
                  :href="userGoogleOauth2OmniauthAuthorizePath()"
                  is-font-small
                  :filled="false"
                  service="google" />
              </li>
            </ul>
          <!-- /.o-list__signInCta --></div>

          <div class="o-note__general">
            <p class="o-note__general--item u-text__center u-text__x-small">
              BizHintがあなたの許可無く投稿することはありません
            </p>
          <!-- /.o-note__general --></div>
        <!-- /.l-unit__maskForGuest action --></div>

        <div class="l-unit__maskForGuest--action is-secondary">
          <p class="p u-text__center u-text__small">
            <a
              class="a"
              :href="forSignUp ? newUserSessionPath() : newUserRegistrationPath()">
              {{ forSignUp ? '会員の方はこちらからログイン' : '非会員の方はこちらから登録（無料）' }}
            </a>
          </p>
        <!-- /.l-unit__maskForGuest action --></div>
      <!-- /.l-unit__maskForGuest --></div>

      <slot :mask-rect="maskRect" />
    <!-- /.f-section__maskForGuest body --></div>
  <!-- /.f-section__maskForGuest --></section>
</template>

<style lang="sass" scoped>
// foundation
.f-section__maskForGuest
  $root: &
  $gradient-height-px: 80

  &[data-fade="true"]
    #{$root}--body
      position: relative // for overlaying the content
      margin-top: -(changeSizePx2Em($gradient-height-px))

      &::before
        content: ""
        width: 100%
        height: changeSizePx2Em($gradient-height-px)
        display: block
        margin-bottom: changeSizePx2Em(math.div($gradient-height-px, 2))
        background-image: linear-gradient(to bottom, rgba($bg-x-light-gray, 0) 0%, rgba($bg-x-light-gray, 1) 100%)

// unit
.l-unit__maskForGuest
  $root: &

  & &--action
    &.is-primary
      padding: 1em 0.5em
      background-color: rgba($bg-white, 1)

    &.is-secondary
      margin-top: 1.5em

      &:first-child
        margin-top: 0

  &[data-balloon="true"]
    #{$root}--header
      margin-bottom: -1em

    #{$root}--action
      &.is-primary
        padding-top: 2em

  +breakpoint
    #{$root}--action
      &.is-secondary
        margin-top: changeSizePx2Em($global-prop-sp)

        &:first-child
          margin-top: 0

// object
.o-heading__maskForGuest
  margin-bottom: 0.5em

  &:last-child
    margin-bottom: 0

  & &--item
    display: flex
    flex-direction: column
    align-items: center

  & &--label
    line-height: 1.6
    font-weight: bold
    text-align: center

    &.is-primary
      font-size: $font-large
      margin-bottom: 0.5em

      &:last-child
        margin-bottom: 0

    &.is-secondary
      position: relative
      color: $font-dark-orange
      font-size: $font-small
      padding: 0.15em changeSizePx2Em(8, $font-small-px)
      background-color: rgba($bg-white, 1)
      border: 1px solid $border-dark-orange

      &::before,
      &::after
        content: ""
        display: block
        position: absolute
        left: 50%
        transform: translateX(-50%)
        clip-path: polygon(0 0, 100% 0, 50% 100%)

      $triangle-width-px: 12
      $triangle-height-px: 6
      &::before
        width: #{$triangle-width-px}px
        height: #{$triangle-height-px}px
        bottom: -#{$triangle-height-px}px
        background-color: rgba($bg-dark-orange, 1)

      $triangle-border-width: 1
      $inner-triangle-width: $triangle-width-px - ($triangle-border-width * 2)
      $inner-triangle-height: math.div($inner-triangle-width, 2)
      &::after
        width: #{$inner-triangle-width}px
        height: #{$inner-triangle-height}px
        bottom: -#{$inner-triangle-height}px
        background-color: rgba($bg-white, 1)

.o-list__signInCta
  $container-width: 280
  max-width: #{$container-width}px
  margin: 0 auto

  & &--items
    display: flex
    flex-wrap: wrap
    justify-content: space-between

  & &--item
    margin-top: 1em

    &:first-child
      margin-top: 0

    &.is-mail
      width: 100%

    &.is-sns
      $sns-button-width: 136
      width: getRelativeValue($sns-button-width, $container-width)

.o-label__maskForGuest
  & &--item
    line-height: 1.6
    font-size: $font-small

// override CSS
.i-mail
  width: 1.3em
  height: 1.3em
  display: block
  margin-right: 0.8em
</style>
